import React, { useState } from 'react'
import {
  User,
  Mail,
  DollarSign,
  Save,
  AlertCircle,
  CheckCircle,
  Shield,
  Info,
  Trash2
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import Loader from '../components/Loader'

import { GeometricBackground } from '../components/layouts/GeometricBackground'

function Profile() {
  const { user, updateUser, logout } = useAuth()
  const [formData, setFormData] = useState({
    name: user?.name || '',
    salary: user?.salary || ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showResetModal, setShowResetModal] = useState(false)
  const [resetting, setResetting] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear messages when user starts typing
    if (error) setError('')
    if (success) setSuccess('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    if (formData.salary && parseFloat(formData.salary) <= 0) {
      setError('Salary must be a positive number')
      return
    }

    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const updates = {};

      // Collect all changes
      if (formData.name !== user?.name) {
        updates.name = formData.name.trim();
      }

      if (formData.salary && parseFloat(formData.salary) !== user?.salary) {
        updates.salary = parseFloat(formData.salary);
      }

      // If there are any updates, send them
      if (Object.keys(updates).length > 0) {
        const response = await api.put('/user/profile', updates);
        updateUser(response.data.user);
        setSuccess('Profile updated successfully!');
      } else {
        setError('No changes to save');
      }
    } catch (err) {
      console.error('❌ Failed to update profile:', err);
      // Improved error handling for 404
      if (err.response?.status === 404) {
        setError('Session invalid. Please log out and log in again.');
      } else {
        setError(err.response?.data?.message || err.userMessage || 'Failed to update profile');
      }
    } finally {
      setLoading(false);
    }
  }

  const handleReset = async () => {
    setResetting(true)
    setError('')
    setSuccess('')

    try {
      await api.post('/user/reset')
      setSuccess('All data has been reset successfully!')
      setShowResetModal(false)

      // Wait a moment to show success message, then navigate to home
      setTimeout(() => {
        window.location.href = '/'
      }, 1500)
    } catch (err) {
      console.error('❌ Failed to reset data:', err)
      setError(err.response?.data?.message || 'Failed to reset data')
      setShowResetModal(false)
    } finally {
      setResetting(false)
    }
  }

  return (
    <div className="relative min-h-screen bg-[#030303]">
      <GeometricBackground variant="default" className="fixed inset-0" />
      <div className="relative z-10 p-4 sm:p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">
            Profile Settings
          </h1>
          <p className="text-white/60">
            Manage your account information and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left column: Profile Form + Account Actions */}
          <div className="lg:col-span-2 space-y-6">
            <div className="glass-card p-6">
              <h2 className="text-lg font-semibold text-white mb-6">
                Personal Information
              </h2>

              {error && (
                <div className="mb-6 flex items-center space-x-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {success && (
                <div className="mb-6 flex items-center space-x-2 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <CheckCircle className="h-5 w-5 text-emerald-400" />
                  <p className="text-sm text-emerald-400">{success}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    <Mail className="h-4 w-4 inline mr-2 text-white/60" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    className="bg-white/5 border border-white/10 text-white/50 text-sm rounded-lg block w-full p-2.5 cursor-not-allowed"
                    disabled
                  />
                  <p className="text-xs text-white/40 mt-1">
                    Email cannot be changed for security reasons
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    <User className="h-4 w-4 inline mr-2 text-white/60" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-white/5 border border-white/10 text-white text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 placeholder-white/30"
                    placeholder="Enter your full name"
                    disabled={loading}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    <DollarSign className="h-4 w-4 inline mr-2 text-white/60" />
                    Monthly Salary (₹)
                  </label>
                  <input
                    type="number"
                    name="salary"
                    value={formData.salary}
                    onChange={handleChange}
                    className="bg-white/5 border border-white/10 text-white text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 placeholder-white/30"
                    placeholder="Enter your monthly salary"
                    min="0"
                    step="0.01"
                    disabled={loading}
                  />
                  <p className="text-xs text-white/40 mt-1">
                    Used to calculate category allocations based on percentages
                  </p>
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <Loader size="sm" variant="white" className="mr-2" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Account Actions — directly below Personal Information */}
            <div className="glass-card p-6">
              <h3 className="font-semibold text-white mb-4">Account Actions</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={logout}
                  className="flex-1 px-4 py-2 bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-lg hover:bg-rose-500/30 transition-colors"
                >
                  Sign Out
                </button>
                <button
                  onClick={() => setShowResetModal(true)}
                  disabled={resetting}
                  className="flex-1 px-4 py-2 bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-lg hover:bg-rose-500/30 transition-colors flex items-center justify-center space-x-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>{resetting ? 'Resetting...' : 'Reset All Data'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Account Info */}
            <div className="glass-card p-6 text-center">
              <h3 className="text-lg font-semibold text-white mb-4">
                Account Information
              </h3>

              <div className="space-y-3">
                <div className="flex items-center justify-center w-16 h-16 bg-primary-500/20 rounded-full mx-auto mb-4 overflow-hidden border-2 border-primary-500/30">
                  {user?.profile_picture ? (
                    <img
                      src={user.profile_picture}
                      alt={user?.name || 'User'}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.classList.add('bg-primary-500/20');
                        e.target.parentElement.innerHTML = `<span class="text-2xl font-bold text-primary-400">${user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}</span>`;
                      }}
                    />
                  ) : (
                    <span className="text-2xl font-bold text-primary-400">
                      {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>

                <div className="text-center">
                  <p className="font-medium text-white">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-sm text-white/50">
                    {user?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Security Notice */}
            <div className="glass-card p-6">
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-primary-400 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-white mb-2">
                    Security &amp; Privacy
                  </h3>
                  <p className="text-sm text-white/60">
                    We never store your bank details or UPI credentials. All payments are handled securely through your UPI app.
                  </p>
                </div>
              </div>
            </div>

            {/* UPI Disclaimer */}
            <div className="glass-card p-6">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-amber-400 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-white mb-2">
                    UPI Payments
                  </h3>
                  <p className="text-sm text-white/60">
                    Payments are processed through your installed UPI apps (Google Pay, PhonePe, etc.). We only track transaction status for budget management.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Reset Confirmation Modal */}
        {showResetModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="glass-card max-w-md w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-12 h-12 bg-rose-500/20 rounded-full">
                  <AlertCircle className="h-6 w-6 text-rose-500" />
                </div>
                <h2 className="text-xl font-bold text-white">
                  Reset All Data?
                </h2>
              </div>

              <div className="mb-6 space-y-3">
                <p className="text-white">
                  This action will permanently delete:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-white/60">
                  <li>All your budget categories</li>
                  <li>All your transaction history</li>
                  <li>All spending data</li>
                </ul>
                <p className="text-sm font-semibold text-rose-400">
                  This action cannot be undone!
                </p>
                <p className="text-sm text-white/60">
                  Your account information (email, name, salary) will be kept.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowResetModal(false)}
                  disabled={resetting}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors font-medium border border-white/10"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReset}
                  disabled={resetting}
                  className="flex-1 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resetting ? 'Resetting...' : 'Yes, Reset Everything'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Profile
